import { useCallback, useEffect, useMemo, useState } from "react";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import { shippingReportsApi } from "../../services/shippingReportsApi";
import { CarrierOption, carriersApi } from "../../services/carriersApi";
import {
  ShippingReport,
  ShippingReportImage,
  ShippingReportImageType,
  ShippingReportTemperatureReading,
  ShippingReportUpdate,
} from "../../types/shippingReports";

export const emptyShippingReportValues: ShippingReportUpdate = {
  arrival_at: null,
  customer_name: "",
  carrier_name: "",
  arrival_temperature_celsius: null,
  driver_name: "",
  trailer_number: "",
  trailer_plate_number: "",
  started_at: null,
  completed_at: null,
  departure_temperature_celsius: null,
  departure_at: null,
  notes: "",
};

const toDateTimeLocal = (value?: string | null) =>
  value ? value.slice(0, 16) : null;

const reportToValues = (report: ShippingReport): ShippingReportUpdate => ({
  arrival_at: toDateTimeLocal(report.local_times?.arrival_at),
  customer_name: report.customer_name ?? "",
  carrier_name: report.carrier_name ?? "",
  arrival_temperature_celsius:
    report.arrival_temperature_celsius ?? null,
  driver_name: report.driver_name ?? "",
  trailer_number: report.trailer_number ?? "",
  trailer_plate_number: report.trailer_plate_number ?? "",
  started_at: toDateTimeLocal(report.local_times?.started_at),
  completed_at: toDateTimeLocal(report.local_times?.completed_at),
  departure_temperature_celsius:
    report.departure_temperature_celsius ?? null,
  departure_at: toDateTimeLocal(report.local_times?.departure_at),
  notes: report.notes ?? "",
});

type Props = {
  reportId?: string;
  logout: () => void;
  addSnackbar: (snackbar: SnackbarData) => void;
};

export function useShippingReportViewModel({
  reportId,
  logout,
  addSnackbar,
}: Props) {
  const [report, setReport] = useState<ShippingReport | null>(null);
  const [values, setValues] = useState<ShippingReportUpdate>(
    emptyShippingReportValues,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingType, setUploadingType] =
    useState<ShippingReportImageType | null>(null);
  const [savingTemperature, setSavingTemperature] = useState(false);
  const [carrierOptions, setCarrierOptions] = useState<CarrierOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const notify = useCallback(
    (message: string, type = SnackbarType.Success) =>
      addSnackbar(
        new SnackbarData(generateUniqueNumber().toString(), message, type),
      ),
    [addSnackbar],
  );

  const handleFailure = useCallback(
    (result: any, fallback: string) => {
      if (result.errorStatus === 401) {
        logout();
      }
      const message = result.data?.message ?? fallback;
      setError(message);
      notify(message, SnackbarType.Error);
    },
    [logout, notify],
  );

  const loadReport = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setError(null);
    const result = await shippingReportsApi.get(reportId);
    setLoading(false);

    if (!result.success) {
      handleFailure(result, "Could not load the shipment report");
      return;
    }

    setReport(result.data);
    setValues(reportToValues(result.data));
    setDirty(false);
  }, [handleFailure, reportId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    carriersApi.dropdown().then((result: any) => {
      if (result.success) setCarrierOptions(result.data ?? []);
      else if (result.errorStatus === 401) logout();
    });
  }, [logout]);

  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  const changeValue = (name: keyof ShippingReportUpdate, value: any) => {
    setValues((current) => ({ ...current, [name]: value }));
    setDirty(true);
  };

  const save = async (showSuccess = true) => {
    if (!reportId) return false;
    setSaving(true);
    setError(null);
    const result = await shippingReportsApi.update(reportId, values);
    setSaving(false);

    if (!result.success) {
      handleFailure(result, "Could not save the shipment report");
      return false;
    }

    setReport(result.data);
    setValues(reportToValues(result.data));
    setDirty(false);
    if (showSuccess) notify("Shipment report saved");
    return true;
  };

  const complete = async () => {
    if (!reportId || !(await save(false))) return;
    setSaving(true);
    const result = await shippingReportsApi.complete(reportId);
    setSaving(false);
    if (!result.success) {
      handleFailure(result, "Could not complete the shipment report");
      return;
    }
    setReport(result.data);
    notify("Shipment report completed");
  };

  const reopen = async () => {
    if (!reportId) return;
    setSaving(true);
    const result = await shippingReportsApi.reopen(reportId);
    setSaving(false);
    if (!result.success) {
      handleFailure(result, "Could not reopen the shipment report");
      return;
    }
    setReport(result.data);
    notify("Shipment report reopened");
  };

  const uploadImages = async (
    type: ShippingReportImageType,
    files: File[],
  ) => {
    if (!reportId || files.length === 0) return;
    setUploadingType(type);
    const result = await shippingReportsApi.uploadImages(reportId, type, files);
    setUploadingType(null);
    if (!result.success) {
      handleFailure(result, `Could not upload ${type} pictures`);
      return;
    }
    setReport((current) =>
      current
        ? {
            ...current,
            images: [...current.images, ...result.data.images],
            audits: result.data.audit
              ? [result.data.audit, ...(current.audits ?? [])]
              : current.audits,
          }
        : current,
    );
    notify(`${files.length} picture${files.length === 1 ? "" : "s"} uploaded`);
  };

  const deleteImage = async (image: ShippingReportImage) => {
    const result = await shippingReportsApi.deleteImage(image.id);
    if (!result.success) {
      handleFailure(result, "Could not delete the picture");
      return;
    }
    setReport((current) =>
      current
        ? {
            ...current,
            images: current.images.filter((item) => item.id !== image.id),
            audits: result.data.audit
              ? [result.data.audit, ...(current.audits ?? [])]
              : current.audits,
          }
        : current,
    );
    notify("Picture deleted");
  };

  const updateImageCaption = async (imageId: number, caption: string) => {
    const result = await shippingReportsApi.updateImage(imageId, { caption });
    if (!result.success) {
      handleFailure(result, "Could not update the picture caption");
      return;
    }
    setReport((current) =>
      current
        ? {
            ...current,
            images: current.images.map((image) =>
              image.id === imageId ? result.data.image : image,
            ),
            audits: result.data.audit
              ? [result.data.audit, ...(current.audits ?? [])]
              : current.audits,
          }
        : current,
    );
  };

  const moveImage = async (
    type: ShippingReportImageType,
    imageId: number,
    direction: -1 | 1,
  ) => {
    if (!reportId || !report) return;
    const category = report.images
      .filter((image) => image.type === type)
      .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    const index = category.findIndex((image) => image.id === imageId);
    const destination = index + direction;
    if (index < 0 || destination < 0 || destination >= category.length) return;
    [category[index], category[destination]] = [
      category[destination],
      category[index],
    ];
    const result = await shippingReportsApi.reorderImages(
      reportId,
      category.map((image) => image.id),
    );
    if (!result.success) {
      handleFailure(result, "Could not reorder the pictures");
      return;
    }
    setReport(result.data);
  };

  const downloadPdf = async () => {
    if (!reportId) return;
    setSaving(true);
    const result = await shippingReportsApi.downloadPdf(reportId);
    setSaving(false);
    if (!result.success) {
      handleFailure(result, "Could not download the PDF");
      return;
    }
    const url = URL.createObjectURL(result.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `shipment-loading-report-${reportId}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const addArrivalTemperature = async (values: {
    temperatureCelsius: string;
    image: File;
    note?: string;
  }) => {
    if (!reportId) return false;
    setSavingTemperature(true);
    const result = await shippingReportsApi.createArrivalTemperature(
      reportId,
      values,
    );
    setSavingTemperature(false);
    if (!result.success) {
      handleFailure(result, "Could not save the arrival temperature reading");
      return false;
    }

    setReport((current) =>
      current
        ? {
            ...current,
            arrival_temperature_readings: [
              ...(current.arrival_temperature_readings ?? []),
              result.data.reading,
            ].sort((a, b) =>
              a.recorded_at.localeCompare(b.recorded_at),
            ),
            audits: result.data.audit
              ? [result.data.audit, ...(current.audits ?? [])]
              : current.audits,
          }
        : current,
    );
    notify("Arrival temperature reading saved");
    return true;
  };

  const deleteArrivalTemperature = async (
    reading: ShippingReportTemperatureReading,
  ) => {
    const result = await shippingReportsApi.deleteArrivalTemperature(reading.id);
    if (!result.success) {
      handleFailure(result, "Could not delete the arrival temperature reading");
      return;
    }

    setReport((current) =>
      current
        ? {
            ...current,
            arrival_temperature_readings:
              (current.arrival_temperature_readings ?? []).filter(
                (item) => item.id !== reading.id,
              ),
            audits: result.data.audit
              ? [result.data.audit, ...(current.audits ?? [])]
              : current.audits,
          }
        : current,
    );
    notify("Arrival temperature reading deleted");
  };

  const imagesByType = useMemo(
    () => ({
      bol: (report?.images ?? []).filter((image) => image.type === "bol"),
      pallet: (report?.images ?? []).filter(
        (image) => image.type === "pallet",
      ),
      tempMonitor: (report?.images ?? []).filter(
        (image) => image.type === "temp_monitor",
      ),
    }),
    [report?.images],
  );

  return {
    report,
    values,
    loading,
    saving,
    uploadingType,
    savingTemperature,
    carrierOptions,
    error,
    dirty,
    imagesByType,
    changeValue,
    save,
    complete,
    reopen,
    uploadImages,
    deleteImage,
    updateImageCaption,
    moveImage,
    downloadPdf,
    addArrivalTemperature,
    deleteArrivalTemperature,
    loadReport,
  };
}
